function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import { buttonClasses } from '@mui/material/Button';
import { bool, func, object, oneOf, string } from 'prop-types';
import clsx from 'clsx';
import { alpha, styled } from '@mui/material/styles';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent, { snackbarContentClasses } from '@mui/material/SnackbarContent';
import SovosButton from '../sovos-button/SovosButton';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const CONFIRMATION = 'confirmation';
const WARNING = 'warning';
const ERROR = 'error';
const SHORT = 'short';
const MEDIUM = 'medium';
const LONG = 'long';
const StyledSnackbar = styled(Snackbar)(_ref => {
  let {
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref;
  return {
    bottom: spacing(3),
    left: spacing(3),
    right: spacing(3),
    transform: 'none',
    pointerEvents: 'none',
    ["& .".concat(snackbarContentClasses.root)]: {
      boxSizing: 'border-box',
      color: palette.primary.contrastText,
      padding: "".concat(spacing(), " ").concat(spacing(2), " 0"),
      minHeight: spacing(6),
      minWidth: spacing(60),
      maxWidth: spacing(85),
      pointerEvents: 'auto',
      ...typography.body1,
      ["&.sovosSnackbar--".concat(CONFIRMATION)]: {
        backgroundColor: palette.primary.main
      },
      ["&.sovosSnackbar--".concat(WARNING)]: {
        color: palette.warning.contrastText,
        backgroundColor: palette.warning.main,
        ["& .".concat(buttonClasses.textPrimary, ".sovosSnackbar__actionButton")]: {
          color: palette.text.primary,
          '&:hover': {
            backgroundColor: alpha(palette.text.primary, palette.action.hoverOpacity)
          }
        }
      },
      ["&.sovosSnackbar--".concat(ERROR)]: {
        backgroundColor: palette.error.main,
        color: palette.error.contrastText
      }
    },
    ["& .".concat(snackbarContentClasses.message)]: {
      padding: 0,
      paddingBottom: spacing()
    },
    ["& .".concat(snackbarContentClasses.action)]: {
      marginRight: 0
    },
    ["& .".concat(buttonClasses.textPrimary, ".sovosSnackbar__actionButton")]: {
      color: palette.primary.contrastText,
      marginBottom: spacing(),
      '&:hover': {
        backgroundColor: alpha(palette.common.white, palette.action.hoverOpacity)
      }
    }
  };
});
const SovosSnackbar = /*#__PURE__*/React.memo(_ref2 => {
  let {
    actionLabel,
    classes,
    className,
    defaultErrorActionLabel,
    message,
    onAction,
    onClose,
    open = false,
    style,
    timeout = MEDIUM,
    variant = CONFIRMATION,
    ...rest
  } = _ref2;
  const {
    t
  } = useMosaicTranslation();
  const getAutoHideDuration = () => {
    switch (timeout) {
      case SHORT:
        return 4000;
      case LONG:
        return 10000;
      case MEDIUM:
      default:
        return 7000;
    }
  };
  const handleOnAction = () => {
    if (onAction) {
      onAction();
    } else {
      onClose();
    }
  };
  const handleOnClose = (event, reason) => {
    if (reason !== 'clickaway') {
      onClose();
    }
  };
  const getSnackBarAction = () => {
    let label = actionLabel;
    if (!actionLabel && variant === ERROR) {
      label = defaultErrorActionLabel || t('close');
    }
    if (label) {
      return /*#__PURE__*/React.createElement(SovosButton, {
        disableRipple: true,
        classes: {
          root: classes === null || classes === void 0 ? void 0 : classes.actionButton
        },
        className: "sovosSnackbar__actionButton",
        onClick: handleOnAction,
        variant: "text"
      }, label);
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(StyledSnackbar, _extends({
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'bottom'
    },
    autoHideDuration: getAutoHideDuration(),
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.root
    },
    className: clsx('sovosSnackbar', className),
    onClose: handleOnClose,
    open: open,
    style: style
  }, rest), /*#__PURE__*/React.createElement(SnackbarContent, {
    className: clsx("sovosSnackbar--".concat(variant.toLowerCase()), classes && classes[variant.toLowerCase()]),
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.content,
      message: classes === null || classes === void 0 ? void 0 : classes.message,
      action: classes === null || classes === void 0 ? void 0 : classes.action
    },
    message: message,
    action: getSnackBarAction()
  }));
});
SovosSnackbar.propTypes = {
  /**
   * When present, a text button will be displayed.
   */
  actionLabel: string,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Error variant text button label
   */
  defaultErrorActionLabel: string,
  /**
   * Message displayed in the snackbar
   */
  message: string.isRequired,
  /**
   * Callback fired when action button is clicked
   */
  onAction: func,
  /**
   * Callback fired when the Snackbar is closed
   */
  onClose: func.isRequired,
  /**
   * If true, snackbar is displayed
   */
  open: bool,
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Short, medium, and long durations are 4, 7, and 10 seconds, respectively
   */
  timeout: oneOf([SHORT, MEDIUM, LONG]),
  /**
   * Variant of the Snackbar
   */
  variant: oneOf([CONFIRMATION, WARNING, ERROR])
};
export default SovosSnackbar;